import { EDITORIAL_REVIEWER_UI_READY } from 'app/config/editorialReviewer.config';
import { useEditorialAccess } from 'app/context/editorialAccess.context';
import { useEditorialResources } from 'app/context/editorialResource.context';
import { getEditorialCapabilities } from 'app/models/editorialCapabilities';
import { runEditorialMutation } from 'app/services/editorialMutation.service';
import React from 'react';

function isSameEditorialScope(previous, current) {
  if (!previous || !current.access || !Array.isArray(current.resources) || !Array.isArray(previous.resources)) return false;
  if (previous.access.role !== current.access.role || previous.access.updateAccess !== current.access.updateAccess) return false;
  const getResources = (resources) => resources.map(({ type, guid }) => ({ type, guid }));
  return JSON.stringify(getResources(previous.resources)) === JSON.stringify(getResources(current.resources));
}

function isEditorialWritePaused(previous, current) {
  if (!isSameEditorialScope(previous, current)) return false;
  const { access, resources } = current;
  if (!access.revalidating && !resources.some((resource) => resource?.revalidating)) return false;
  return getEditorialCapabilities(
    { ...access, revalidating: false },
    resources.map((resource) => ({ ...resource, revalidating: false }))
  ).canWrite;
}

export default function useEditorialCapabilities() {
  const access = useEditorialAccess();
  const resources = useEditorialResources();
  const snapshot = React.useMemo(() => ({ access, resources }), [access, resources]);
  const latest = React.useRef(snapshot);
  latest.current = snapshot;
  const lifecycle = React.useRef(null);

  React.useEffect(() => {
    const controller = new AbortController();
    lifecycle.current = controller;
    return () => controller.abort();
  }, [snapshot]);

  const mutate = React.useCallback((operation) => {
    if (!EDITORIAL_REVIEWER_UI_READY) return Promise.resolve().then(operation);
    const signal = lifecycle.current?.signal;
    if (!signal) return Promise.resolve({ cancelled: true, error: null });
    return runEditorialMutation({
      getSnapshot: () => latest.current,
      operation,
      signal,
      onAccess: latest.current.access.updateAccess,
      onDenied: latest.current.access.retry,
    });
  }, []);

  const resolvedScope = React.useRef(null);
  const capabilities = EDITORIAL_REVIEWER_UI_READY ? getEditorialCapabilities(access, resources) : { canRead: true, canWrite: true, readOnly: false };
  const writePaused = EDITORIAL_REVIEWER_UI_READY && !capabilities.canWrite && isEditorialWritePaused(resolvedScope.current, snapshot);
  if (capabilities.canWrite) resolvedScope.current = snapshot;
  else if (!writePaused) resolvedScope.current = null;
  // readOnly describe la vista, no la guarda: durante la pausa por revalidación la UI sigue editable y
  // solo la persistencia espera (mutate). Así un cambio de pestaña no oculta «Guardar» ni bloquea campos.
  return { ...capabilities, readOnly: capabilities.readOnly && !writePaused, writePaused, mutate };
}
